import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCompanyPreferences,
  updateCompanyPreferences,
  getCompanyDiscounts,
  updateCompanyDiscounts,
  getCompanyFinancialYears,
  updateCompanyFYLocking,
  changeCompanyPassword,
  downloadCompanyBackup,
  getModulesCatalog,
  getUserPermissions,
  updateUserPermissions,
} from "services/companyAdmin";

export const useCompanyPreferences = () => {
  return useQuery({
    queryKey: ["company-admin-preferences"],
    queryFn: () => getCompanyPreferences(),
    staleTime: 1000 * 60 * 5,
  });
};

export const useUpdateCompanyPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => updateCompanyPreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-admin-preferences"] });
      queryClient.invalidateQueries({
        queryKey: ["company-admin-modules-catalog"],
      });
    },
  });
};

export const useCompanyDiscounts = () => {
  return useQuery({
    queryKey: ["company-admin-discounts"],
    queryFn: () => getCompanyDiscounts(),
    staleTime: 1000 * 60 * 5,
  });
};

export const useUpdateCompanyDiscounts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => updateCompanyDiscounts(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-admin-discounts"] });
    },
  });
};

export const useCompanyFinancialYears = () => {
  return useQuery({
    queryKey: ["company-admin-financial-years"],
    queryFn: () => getCompanyFinancialYears(),
    staleTime: 1000 * 60 * 5,
  });
};

export const useUpdateCompanyFYLocking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => updateCompanyFYLocking(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["company-admin-financial-years"],
      });
    },
  });
};

export const useChangeCompanyPassword = () => {
  return useMutation({
    mutationFn: (data) => changeCompanyPassword(data),
  });
};

export const useDownloadCompanyBackup = () => {
  return useMutation({
    mutationFn: () => downloadCompanyBackup(),
  });
};

export const useModulesCatalog = () => {
  return useQuery({
    queryKey: ["company-admin-modules-catalog"],
    queryFn: () => getModulesCatalog(),
    staleTime: 1000 * 60 * 30,
  });
};

export const useUserPermissions = (userId) => {
  return useQuery({
    queryKey: ["company-admin-user-permissions", userId],
    queryFn: () => getUserPermissions(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useUpdateUserPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }) => updateUserPermissions(userId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["company-admin-user-permissions", variables.userId],
      });
    },
  });
};
